// Global script loader utility to prevent duplicate script loading and conflicts

const loadedScripts = new Map()

/**
 * Loads a script only once and returns a promise
 * @param {string} src - The script source URL
 * @param {Object} options - Additional options for the script
 * @returns {Promise} Promise that resolves when script is loaded
 */
export const loadScriptOnce = (src, options = {}) => {
  // Check if script is already loaded
  if (loadedScripts.has(src)) {
    return loadedScripts.get(src)
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector(`script[src="${src}"]`)
  if (existingScript) {
    const promise = Promise.resolve()
    loadedScripts.set(src, promise)
    return promise
  }

  // Create new script element
  const script = document.createElement('script')
  script.src = src
  script.async = options.async !== false
  script.defer = options.defer || false

  // Create promise for script loading
  const promise = new Promise((resolve, reject) => {
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      console.error(`Failed to load script: ${src}`)
      loadedScripts.delete(src)
      reject(new Error(`Failed to load script: ${src}`))
    }
  })

  // Store the promise
  loadedScripts.set(src, promise)

  // Append script to document
  document.body.appendChild(script)

  return promise
}

/**
 * Checks if a script is already loaded
 * @param {string} src - The script source URL
 * @returns {boolean} True if script is loaded
 */
export const isScriptLoaded = src => {
  return (
    loadedScripts.has(src) || !!document.querySelector(`script[src="${src}"]`)
  )
}

/**
 * Removes a script from the loaded scripts cache (but doesn't remove from DOM)
 * @param {string} src - The script source URL
 */
export const removeScriptFromCache = src => {
  loadedScripts.delete(src)
}

/**
 * Clears all script cache (useful for testing or cleanup)
 */
export const clearScriptCache = () => {
  loadedScripts.clear()
}
